<template>
  <div class="columns is-multiline">
    <div class="column is-12">
      <div class="content">
        <h3>{{ i18n.bookings[iso] }}</h3>
      </div>
      <b-table
        :data="bookings"
        :loading="loading"
        :striped="true"
        :narrowed="true"
        :hoverable="true"
        :paginated="bookings.length > 10"
        :per-page="10"
        pagination-simple
        :empty-label="iso === 'es' ? 'No hay reservaciones asociadas' : 'No associated bookings'"
      >
        <b-table-column field="reference" :label="iso === 'es' ? 'Referencia' : 'Reference'" centered sortable v-slot="props">
          <b>{{ props.row.reference }}</b>
        </b-table-column>
        <b-table-column field="channel" :label="iso === 'es' ? 'Canal' : 'Channel'" centered sortable v-slot="props">
          <b-tag :type="props.row.channel === 'Online' ? 'is-success' : 'is-link'">
            {{ props.row.channel }}
          </b-tag>
        </b-table-column>
        <b-table-column field="hotel" :label="iso === 'es' ? 'Hotel / Servicio' : 'Hotel / Service'" centered v-slot="props">
          {{ props.row.hotel }}
          <small v-if="props.row.tripType" class="has-text-grey is-block">{{ tripTypeLabel(props.row.tripType) }}</small>
        </b-table-column>
        <b-table-column field="checkin" label="Check-in / Sail" centered sortable v-slot="props">
          {{ formatDate(props.row.checkin) }}
          <small v-if="props.row.isCruise" class="has-text-grey is-block">Sail</small>
        </b-table-column>
        <b-table-column field="checkout" label="Check-out / Return" centered v-slot="props">
          {{ formatDate(props.row.checkout) }}
          <small v-if="props.row.isCruise" class="has-text-grey is-block">Return</small>
        </b-table-column>
        <b-table-column field="createdAt" :label="iso === 'es' ? 'Creación' : 'Created'" centered sortable v-slot="props">
          {{ formatDate(props.row.createdAt) }}
        </b-table-column>
        <b-table-column field="status" :label="iso === 'es' ? 'Estado' : 'Status'" centered v-slot="props">
          <b-tag v-if="props.row.status" :type="statusType(props.row.status)">
            {{ props.row.status }}
          </b-tag>
        </b-table-column>
        <b-table-column field="paymentStatus" :label="iso === 'es' ? 'Pago' : 'Payment'" centered v-slot="props">
          <b-tag v-if="props.row.paymentStatus" :type="statusType(props.row.paymentStatus)">
            {{ props.row.paymentStatus }}
          </b-tag>
        </b-table-column>
        <b-table-column field="amount" :label="iso === 'es' ? 'Monto' : 'Amount'" centered sortable v-slot="props">
          {{ formatAmount(props.row.amount) }} {{ props.row.currency }}
        </b-table-column>
      </b-table>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import i18n from "../../utils/i18n";
const API_URL = process.env.VUE_APP_API_URL;

export default {
  name: "MemberBookings",
  props: {
    loginId: {
      type: String,
      default: "",
    },
    iso: {
      type: String,
      default: "en",
    },
    // Solo consulta cuando la pestaña que lo contiene esta activa
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      i18n,
      bookings: [],
      loading: false,
      loadedLoginId: null,
    };
  },
  watch: {
    active: {
      handler: "loadIfNeeded",
      immediate: true,
    },
    loginId: "loadIfNeeded",
  },
  methods: {
    loadIfNeeded() {
      if (this.active && this.loginId && this.loginId !== this.loadedLoginId) this.getBookings();
    },
    async getBookings() {
      const LoginID = this.loginId;
      try {
        this.loading = true;
        const [bookingsRes, weeksRes] = await Promise.allSettled([
          // /bookings/list filtra por LoginID y agrega salesChannel (Online / Call Center) y paymentStatus
          axios.post(`${API_URL}/bookings/list?limit=200`, { LoginID }),
          // Las semanas viven en otra coleccion; /reserve-vrsys filtra por client.loginId
          axios.post(`${API_URL}/reserve-vrsys?limit=200&page=1`, { LoginID }),
        ]);

        if (bookingsRes.status === "rejected" && weeksRes.status === "rejected") throw bookingsRes.reason;

        const bookings = (bookingsRes.value?.data?.bookings || []).map(this.normalizeBooking);
        // /reserve-vrsys tambien regresa bookings RCI (type "manual"), que ya vienen de /bookings/list
        const weeks = (weeksRes.value?.data?.reserves || [])
          .filter((row) => row.type === "weeks")
          .map(this.normalizeWeek);

        this.bookings = [...bookings, ...weeks].sort((a, b) => this.dateValue(b.createdAt) - this.dateValue(a.createdAt));
        this.loadedLoginId = LoginID;
      } catch (err) {
        console.log(err);
        this.$buefy.toast.open({
          message: this.iso === "es" ? "Error al consultar reservaciones" : "Error fetching bookings",
          type: "is-danger",
        });
      } finally {
        this.loading = false;
      }
    },
    // Unifica reservas TC (VRE, datos en booking.*) y manuales (VRM, datos en la raiz)
    normalizeBooking(row) {
      const tc = row.booking || {};
      const hotelService = (tc.hotelservice || [])[0] || {};
      // Cruceros manuales: naviera y fechas Sail / Return viven en cruiseservice[0].cruiseData
      const cruise = (tc.cruiseservice || [])[0] || null;
      const cruiseData = cruise?.cruiseData || {};
      const operatorTotal = tc.pricebreakdown?.totalPrice?.operator || {};

      return {
        reference: row.ReferenciaReserva || "",
        channel: row.salesChannel || "",
        isCruise: Boolean(cruise),
        hotel: row.NombreHotel || hotelService.hotelName || cruiseData.vendorName || cruise?.providerDescription || "",
        tripType: tc.tripType || row.tripType || "",
        checkin: row.Checkin || hotelService.startDate || cruiseData.sailDate || cruise?.startDate || tc.startDate || null,
        checkout: row.Checkout || hotelService.endDate || cruiseData.returnDate || cruise?.endDate || tc.endDate || null,
        createdAt: row.FechaCreacion || tc.creationDate || null,
        status: tc.status || row.Estado || "",
        paymentStatus: row.paymentStatus?.status || "",
        amount: Number(row.totalPriceCalculated ?? row.Monto ?? operatorTotal.amount) || 0,
        currency: String(row.Moneda || operatorTotal.currency || "USD").toUpperCase(),
      };
    },
    // Reservas de semanas: datos del resort en week.*, estatus NW / RR / SC / CX
    normalizeWeek(row) {
      const week = row.week || {};
      const status = { RR: "BOOKED", CX: "CANCELED" }[row.status] || row.status || "";

      return {
        reference: row.numberRef || "",
        channel: row.salesChannel || (row.createdByEmail ? "Call Center" : "Online"),
        isCruise: false,
        hotel: week.ResortName || "",
        tripType: "WEEK",
        checkin: week.CheckInDate || null,
        checkout: week.CheckOutDate || null,
        createdAt: row.createdAtMs || null,
        status,
        paymentStatus: row.pay ? "paid" : "pending",
        amount: Number(row.price ?? week.Price ?? row.breakdown?.total) || 0,
        currency: "USD",
      };
    },
    dateValue(value) {
      if (!value) return 0;
      return typeof value === "string" ? moment.parseZone(value).valueOf() : Number(value) || 0;
    },
    formatDate(value) {
      if (!value) return "";
      // parseZone respeta la zona del texto: "2027-10-09T00:00:00Z" no se recorre al dia anterior
      const date = typeof value === "string" ? moment.parseZone(value) : moment(value);
      return date.locale(this.iso).format("MMMM D, YYYY");
    },
    tripTypeLabel(tripType) {
      return i18n.bookingTripTypes[tripType]?.[this.iso] || tripType;
    },
    formatAmount(value) {
      return Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    statusType(status) {
      const value = String(status).toLowerCase();
      if (["canceled", "cancelled"].includes(value)) return "is-danger";
      if (["paid", "booked"].includes(value)) return "is-success";
      if (value === "partial") return "is-warning";
      return "is-light";
    },
  },
};
</script>
